import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Swal from 'sweetalert2'
import useCertificationsApi from './useCertificationsApi'

export default function useCertificationsData() {
	const { t } = useTranslation()
	const { deleteCertification, getAllCertifications, searchCertifications } = useCertificationsApi()
	const defaultLimit = 50
	const limitIncrement = 50
	const defaultSortColumn = '_id'
	const defaultSortDirection = 'desc'

	const [data, setData] = useState([])
	const [loading, setLoading] = useState(true)
	const [loadingMore, setLoadingMore] = useState(false)
	const [error, setError] = useState(null)
	const [totalCount, setTotalCount] = useState(0)
	const [hasMore, setHasMore] = useState(false)
	const [limit, setLimit] = useState(defaultLimit)
	const [filters, setFilters] = useState({})
	const [sortColumn, setSortColumn] = useState(defaultSortColumn)
	const [sortDirection, setSortDirection] = useState(defaultSortDirection)
	const loadingMoreRef = useRef(false)

	const loadData = useCallback(async () => {
		if (!loadingMoreRef.current) setLoading(true)
		setError(null)

		try {
			const searchText = `${filters.searchText || ''}`.trim()
			const requestOptions = {
				limit,
				sortColumn,
				sortDirection,
				filters: {},
			}
			const result = searchText
				? await searchCertifications(searchText, requestOptions)
				: await getAllCertifications(requestOptions)

			if (result?.error) {
				setError(result.error)
				setData([])
				setTotalCount(0)
				setHasMore(false)
			} else {
				setData(Array.isArray(result?.data) ? result.data : [])
				setTotalCount(result?.totalCount || 0)
				setHasMore(!!result?.hasMore)
			}
		} catch (error) {
			console.error('useCertificationsData.loadData error:', error)
			setError(error?.message || 'Failed to load certifications')
			setData([])
			setTotalCount(0)
			setHasMore(false)
		} finally {
			loadingMoreRef.current = false
			setLoading(false)
			setLoadingMore(false)
		}
	}, [filters, getAllCertifications, limit, searchCertifications, sortColumn, sortDirection])

	const clearError = useCallback(() => setError(null), [])

	useEffect(() => {
		if (error) {
			Swal.fire({
				title: t('notifications.errorTitle'),
				text: error,
				icon: 'error',
			}).then(() => clearError())
		}
	}, [clearError, error, t])

	useEffect(() => {
		loadData()
	}, [loadData])

	const handleLoadMore = useCallback(() => {
		if (hasMore && !loadingMoreRef.current) {
			loadingMoreRef.current = true
			setLimit(prev => prev + limitIncrement)
			setLoadingMore(true)
		}
	}, [hasMore])

	const setSorting = useCallback((column, direction) => {
		setSortColumn(column)
		setSortDirection(direction)
	}, [])

	const updateFilters = useCallback(newFilters => {
		setFilters(prev => ({ ...prev, ...newFilters }))
	}, [])

	const handleSort = useCallback(column => {
		const newDirection = sortColumn === column && sortDirection === 'asc' ? 'desc' : 'asc'
		setSorting(column, newDirection)
	}, [setSorting, sortColumn, sortDirection])

	const handleSearchChange = useCallback(event => {
		updateFilters({ searchText: event.target.value })
	}, [updateFilters])

	const handleDelete = useCallback(async certification => {
		const certificationName = certification?.name || '—'
		const result = await Swal.fire({
			title: t('trainingCertifications.list.deleteDialog.confirmTitle'),
			text: t('trainingCertifications.list.deleteDialog.confirmText', { name: certificationName }),
			icon: 'warning',
			showCancelButton: true,
			confirmButtonText: t('trainingCertifications.list.deleteDialog.confirmButton'),
			cancelButtonText: t('trainingCertifications.list.deleteDialog.cancelButton'),
			confirmButtonColor: '#dc2626',
		})

		if (!result.isConfirmed) return

		const deleteResult = await deleteCertification(certification?._id)
		if (!deleteResult.success) {
			await Swal.fire({
				title: t('trainingCertifications.list.deleteDialog.errorTitle'),
				text: deleteResult.error || t('trainingCertifications.list.deleteDialog.errorFallback'),
				icon: 'error',
			})
			return
		}

		await loadData()
	}, [deleteCertification, loadData, t])

	return {
		data,
		totalCount,
		hasMore,
		loading,
		loadingMore,
		error,
		sortColumn,
		sortDirection,
		searchQuery: filters.searchText || '',
		loadData,
		handleLoadMore,
		handleSort,
		handleSearchChange,
		handleDelete,
		setSorting,
		updateFilters,
		clearError,
	}
}
